import React, { useMemo, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { CrewPilotInlineSuccess } from "../CrewPilotInlineSuccess";
import { CPInput } from "../CPInput";
import { CPDropdown } from "../CPDropdown";
import {
  CrewPilotNavList,
  CrewPilotNavRow,
  CrewPilotNavSectionHeader,
} from "../CrewPilotNavRow";
import {
  computeEstimateTotals,
  computeLocationTotal,
  computeWorkTypeTotalSqft,
  formatCurrency,
} from "../../helpers/estimateTotals";
import { formatEstimateStatus, formatLocationLabel } from "../../helpers/estimates";
import { presentedSubtotal } from "../../helpers/estimateTax";
import { LocationNameSheet } from "./LocationNameSheet";
import { useIsDesktopViewport } from "../../hooks/useIsDesktopViewport";
import { EstimateWorkspaceDesktop } from "./desktop/EstimateWorkspaceDesktop";
import "./estimateWorkspace.css";

const CREATE_NEW_CLIENT_VALUE = "__create_new_client__";
const CREATE_NEW_CLIENT_OPTION = {
  value: CREATE_NEW_CLIENT_VALUE,
  label: "➕ Create New Client",
};

function locationSqft(location) {
  return (location.workTypes || []).reduce(
    (sum, wt) => sum + computeWorkTypeTotalSqft(wt),
    0,
  );
}

function locationSubtitle(location) {
  const featureCount = (location.workTypes || []).length;
  const sqft = locationSqft(location);
  const parts = [`${featureCount} feature${featureCount === 1 ? "" : "s"}`];
  if (sqft > 0) {
    parts.push(`${sqft.toFixed(0)} ft²`);
  }
  return parts.join(" · ");
}

/**
 * SCREEN 1 — Estimate Home.
 *
 * Compact identity (Estimate name / Client / number+status), a compact
 * total row, Locations as navigable rows, Pricing & Review as its own
 * destination.
 *
 * Job-Wide/General: the auto-created default-scope Location is not shown as
 * a normal Location here when it has no Features on it — that record still
 * exists (deletion-protected, unchanged persistence), it's just not useful
 * workspace UI when empty since whole-estimate charges belong in Pricing &
 * Review. If it DOES have Feature/Measurement/Material/Labour data on it
 * (e.g. from before this presentation change), it is shown as a compact,
 * clearly-labeled row using its existing name so that data is never
 * orphaned or hidden.
 */
export function EstimateWorkspaceScreen({
  estimateTree,
  materialsCatalog,
  clients,
  clientId,
  estimateNumber,
  projectName,
  status,
  editable,
  saving,
  builderError,
  showSaveSuccess,
  onDismissSaveSuccess,
  onClientChange,
  onEstimateNumberChange,
  onProjectNameChange,
  onSaveHeader,
  onBack,
  onOpenLocation,
  onAddLocation,
  onOpenPricing,
}) {
  const isDesktop = useIsDesktopViewport();
  const [showEditDetails, setShowEditDetails] = useState(false);
  const [showAddLocation, setShowAddLocation] = useState(false);

  const totals = useMemo(
    () => computeEstimateTotals(estimateTree, materialsCatalog),
    [estimateTree, materialsCatalog],
  );
  const estimateTotal = presentedSubtotal(estimateTree, totals);

  const locations = estimateTree?.locations || [];
  const physicalLocations = locations.filter((l) => !l.is_default_scope);
  const jobWideLocation = locations.find((l) => l.is_default_scope);
  const jobWideHasData = Boolean(
    jobWideLocation && (jobWideLocation.workTypes || []).length > 0,
  );

  const clientName =
    estimateTree?.clients?.client_name ||
    clients.find((c) => c.client_id === clientId)?.client_name ||
    "—";

  if (isDesktop) {
    const rows = [
      ...physicalLocations,
      ...(jobWideHasData ? [jobWideLocation] : []),
    ].map((location) => ({
      id: location.room_id,
      name: formatLocationLabel(location),
      featureCount: (location.workTypes || []).length,
      sqft: locationSqft(location),
      total: computeLocationTotal(location, materialsCatalog),
      jobWide: location.is_default_scope === true,
    }));

    const headerForm = editable ? (
      <div className="est-d-stack">
        <CPDropdown
          label="Client"
          value={clientId}
          onChange={onClientChange}
          options={[
            CREATE_NEW_CLIENT_OPTION,
            ...clients.map((client) => ({
              value: client.client_id,
              label: client.client_name,
            })),
          ]}
          placeholder="Select client"
        />
        <CPInput
          label="Estimate Name"
          value={estimateNumber}
          onChange={onEstimateNumberChange}
          placeholder="EST-20260619-1234"
          required
        />
        <CPInput
          label="Project Name"
          value={projectName}
          onChange={onProjectNameChange}
          placeholder="Smith Primary Bath Renovation"
          required
        />
        {showSaveSuccess && (
          <CrewPilotInlineSuccess message="Estimate Saved" onDismiss={onDismissSaveSuccess} />
        )}
        <button
          type="button"
          className="dt-btn dt-btn--primary est-d-btn-block"
          onClick={onSaveHeader}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save Estimate"}
        </button>
      </div>
    ) : (
      <dl className="dp-kv dp-kv--tight">
        <dt>Client</dt>
        <dd>{clientName}</dd>
        <dt>Name</dt>
        <dd>{estimateNumber || estimateTree?.estimate_number || "—"}</dd>
        <dt>Project</dt>
        <dd>{projectName || estimateTree?.title || "—"}</dd>
      </dl>
    );

    return (
      <EstimateWorkspaceDesktop
        title={projectName || estimateTree?.title || "Estimate"}
        clientName={clientName}
        estimateNumber={estimateNumber || estimateTree?.estimate_number || ""}
        status={status}
        total={estimateTotal}
        rows={rows}
        editable={editable}
        builderError={builderError}
        onBack={onBack}
        onOpenLocation={onOpenLocation}
        onAddLocation={() => setShowAddLocation(true)}
        onOpenPricing={onOpenPricing}
        headerForm={headerForm}
      >
        {showAddLocation && (
          <LocationNameSheet
            estimateId={estimateTree.estimate_id}
            sortOrder={(estimateTree.locations || []).length + 1}
            onClose={() => setShowAddLocation(false)}
            onSaved={onAddLocation}
          />
        )}
      </EstimateWorkspaceDesktop>
    );
  }

  return (
    <CrewPilotModuleShell
      title={projectName || estimateTree?.title || "Estimate"}
      sectionLabel="Estimate"
      onBack={onBack}
    >
      {builderError && <div className="est-inline-error">{builderError}</div>}

      <div className="est-page-header">
        <div className="est-home-client-row">
          <div className="est-home-field-label">Client</div>
          <div className="est-home-client-value">{clientName}</div>
        </div>

        <div className="est-page-subtitle-row">
          <span>{estimateNumber || estimateTree?.estimate_number || ""}</span>
          <span className="est-status-pill">{formatEstimateStatus(status)}</span>
        </div>

        {editable && (
          <button
            type="button"
            className="est-home-edit-toggle"
            onClick={() => setShowEditDetails((v) => !v)}
          >
            {showEditDetails ? "Hide details" : "Edit details"}
          </button>
        )}
      </div>

      {editable && showEditDetails && (
        <div className="est-home-edit-panel">
          <p className="est-home-edit-note">* Indicates required information</p>
          <CPDropdown
            label="Client"
            value={clientId}
            onChange={onClientChange}
            options={[
              CREATE_NEW_CLIENT_OPTION,
              ...clients.map((client) => ({
                value: client.client_id,
                label: client.client_name,
              })),
            ]}
            placeholder="Select client"
          />
          <CPInput
            label="Estimate Name"
            value={estimateNumber}
            onChange={onEstimateNumberChange}
            placeholder="EST-20260619-1234"
            required
          />
          <CPInput
            label="Project Name"
            value={projectName}
            onChange={onProjectNameChange}
            placeholder="Smith Primary Bath Renovation"
            required
          />
          {showSaveSuccess && (
            <CrewPilotInlineSuccess
              message="Estimate Saved"
              onDismiss={onDismissSaveSuccess}
            />
          )}
          <button
            type="button"
            className="est-sheet-btn est-sheet-btn--primary"
            onClick={onSaveHeader}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Estimate"}
          </button>
        </div>
      )}

      <div
        className={
          "est-total-row" + (estimateTotal > 0 ? " est-total-row--emphasis" : "")
        }
      >
        <span className="est-total-row-label">Estimate Total</span>
        <span className="est-total-row-value">{formatCurrency(estimateTotal)}</span>
      </div>

      <CrewPilotNavSectionHeader>Locations</CrewPilotNavSectionHeader>

      {physicalLocations.length === 0 ? (
        editable ? (
          <button
            type="button"
            className="est-add-first-btn"
            onClick={() => setShowAddLocation(true)}
          >
            + Add First Location
          </button>
        ) : (
          <div className="est-home-empty-row">No locations yet.</div>
        )
      ) : (
        <>
          <CrewPilotNavList>
            {physicalLocations.map((location) => (
              <CrewPilotNavRow
                key={location.room_id}
                title={formatLocationLabel(location)}
                subtitle={locationSubtitle(location)}
                trailingText={
                  computeLocationTotal(location, materialsCatalog) > 0
                    ? formatCurrency(computeLocationTotal(location, materialsCatalog))
                    : null
                }
                onClick={() => onOpenLocation(location.room_id)}
              />
            ))}
          </CrewPilotNavList>

          {editable && (
            <button
              type="button"
              className="est-add-row-btn"
              onClick={() => setShowAddLocation(true)}
            >
              + Add Location
            </button>
          )}
        </>
      )}

      {jobWideHasData && (
        <div className="est-secondary-section">
          <CrewPilotNavSectionHeader>Job-Wide / General</CrewPilotNavSectionHeader>
          <CrewPilotNavList>
            <CrewPilotNavRow
              title={formatLocationLabel(jobWideLocation)}
              subtitle={locationSubtitle(jobWideLocation)}
              trailingText={
                computeLocationTotal(jobWideLocation, materialsCatalog) > 0
                  ? formatCurrency(
                      computeLocationTotal(jobWideLocation, materialsCatalog),
                    )
                  : null
              }
              onClick={() => onOpenLocation(jobWideLocation.room_id)}
            />
          </CrewPilotNavList>
        </div>
      )}

      <CrewPilotNavSectionHeader>Estimate</CrewPilotNavSectionHeader>
      <CrewPilotNavList>
        <CrewPilotNavRow title="Pricing & Review" onClick={onOpenPricing} />
      </CrewPilotNavList>

      <div className="est-bottom-spacer" />

      {showAddLocation && (
        <LocationNameSheet
          estimateId={estimateTree.estimate_id}
          sortOrder={(estimateTree.locations || []).length + 1}
          onClose={() => setShowAddLocation(false)}
          onSaved={onAddLocation}
        />
      )}
    </CrewPilotModuleShell>
  );
}
